<template>
  <!-- Rebuilt on the KarroFin components. Same endpoint, same params, same
       Vuex filter module -- only the presentation moved. -->
  <div class="kf-page">
    <!-- The count tabs become the standard strip. They are still the primary
         filter: pressing one sets `insurance_status` and reloads, exactly as
         the tabs did. The per-bucket colour the API sends is dropped -- the
         strip carries one brand treatment so every screen's counts read the
         same way. -->
    <!-- The bucket strip IS the status filter here. When an admin has pinned
         this user's status it narrows to their bucket and stops responding to
         clicks, rather than disappearing -- the count is theirs to see. -->
    <StatStrip
      v-if="bucketItems.length"
      :items="bucketItems"
      :active="filters.insurance_status"
      :loading="table_loading"
      @select="selectStatus"
    />

    <!-- Filters used to be folded inside a collapsed "Filters" accordion, so
         you could not see what was applied without opening it. The bar shows
         them inline. -->
    <FilterBar
      :search.sync="search_query"
      :show-date="false"
      search-placeholder="Search by name, email or mobile"
      @change="reload()"
      @reset="resetFilters"
    >
      <div v-if="!filterLocked('insurance', 'signup_range')" class="kf-field">
        <span class="kf-field__label">Signup date</span>
        <DateField
          v-model="filters.date_range"
          range
          @change="reload()"
        />
      </div>
      <div v-if="!filterLocked('insurance', 'enquiry_range')" class="kf-field">
        <span class="kf-field__label">From &amp; to date</span>
        <DateField
          v-model="filters.from_date_range"
          range
          @change="reload()"
        />
      </div>
      <div
        v-if="!filterLocked('insurance', 'insurance_product')"
        class="kf-field"
      >
        <span class="kf-field__label">Product type</span>
        <v-select
          v-model="filters.product"
          :items="product_list"
          item-text="title"
          item-value="id"
          placeholder="Any product"
          dense
          outlined
          single-line
          clearable
          hide-details
        ></v-select>
      </div>
    </FilterBar>

    <DataTable
      title="Insurance"
      :headers="headers"
      :items="insurance_list"
      :loading="table_loading"
      :page="filters.page_number"
      :total-pages="total_page_count"
      :total-count="total_count"
      empty-title="No insurance records match these filters"
      empty-message="Try another bucket above, a wider date range, or clearing the search."
      empty-icon="circle-check"
      @page-change="onPage"
    >
      <template #[`item.first_name`]="{ item }">
        <span>{{ item.first_name }}</span> &nbsp;
        <span>{{ item.last_name }}</span>
      </template>

      <template #[`item.status`]="{ item }">
        <StatusChip :status="item.status" small />
      </template>

      <template #[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-center">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click.stop="copyOfferLink(item.utm_link)"
              >
                <LucideIcon name="copy" :size="18" />
              </v-btn>
            </template>
            <span>Copy UTM Link</span>
          </v-tooltip>
          <!-- Converting and rejecting only make sense from the Interested
               bucket, so they appear only there -- unchanged. -->
          <template v-if="filters.insurance_status == 'Interested'">
            <v-btn
              icon
              small
              class="ml-1"
              color="success"
              title="Mark converted"
              @click.stop="openConformationDialog(item, 'Converted')"
            >
              <v-icon>mdi-check-circle-outline</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              class="ml-1"
              color="error"
              title="Mark rejected"
              @click.stop="openConformationDialog(item, 'Rejected')"
            >
              <v-icon>mdi-close-circle-outline</v-icon>
            </v-btn>
          </template>
        </div>
      </template>
    </DataTable>

    <ConformationDialog
      ref="conformation_dialog"
      @update="getUserInsuranceList"
    />
  </div>
</template>
<script>
import {
  getDefaultSignupDateRangeIso,
  formatSignupDateRangeDisplay,
} from "@/utils/defaultSignupDateRange";
import { mapGetters } from "vuex";
import api from "@/app-js/karrofin/api";
import ConformationDialog from "@/components/insurance/dialogs/statusConformationDialog";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import DataTable from "@/components/karrofin/DataTable.vue";
import DateField from "@/components/karrofin/DateField.vue";
import StatStrip from "@/components/karrofin/StatStrip.vue";
import StatusChip from "@/components/karrofin/StatusChip.vue";

export default {
  components: {
    ConformationDialog,
    FilterBar,
    DataTable,
    DateField,
    StatStrip,
    StatusChip,
  },
  data() {
    return {
      insurance_list: [],
      tab_data: [],
      table_loading: false,
      page_number: 1,
      product_list: [],
      total_page_count: 0,
      total_count: null,
      /* Owned by the page now rather than read from the shared navbar search. */
      search_query: "",
      today: new Date().toISOString().substr(0, 10),
      headers: [
        { text: "ID", align: "start", value: "id", width: 90 },
        { text: "Name", align: "start", value: "first_name", width: 220 },
        { text: "Email", align: "start", value: "email", width: 260 },
        { text: "Mobile", align: "start", value: "mobile", width: 150 },
        { text: "Status", align: "start", value: "status", width: 170 },
        { text: "Action", align: "center", value: "actions", width: 160 },
      ],
    };
  },
  mounted() {
    this.getUserInsuranceList();
    this.getProductList();
  },
  computed: {
    /* The filter object is raw Vuex state, written straight through v-model --
       which is what makes these filters survive leaving the screen. Kept. */
    ...mapGetters({
      filters: "insurance/getFilters",
    }),
    bucketItems() {
      const all = this.tab_data.map((item) => ({
        label: item.title,
        value: item.count || 0,
        filter: item.param,
      }));
      const pinned = this.lockedValues("insurance", "status");
      if (!pinned) return all;
      /* Dropping `filter` is what makes StatStrip read-only: it turns itself
         non-interactive once no item carries one. */
      const kept = all.filter((item) => pinned.indexOf(item.filter) !== -1);
      return kept.length
        ? kept.map((item) => ({ label: item.label, value: item.value }))
        : all;
    },
  },
  methods: {
    /* StatStrip emits null when the active segment is pressed again. The tabs
       this replaces had no un-selected state, so that press stays a no-op. */
    selectStatus(value) {
      if (value === null) return;
      this.filters.page_number = 1;
      this.filters.insurance_status = value;
      this.getUserInsuranceList();
    },
    onPage(page) {
      this.filters.page_number = page;
      this.getUserInsuranceList();
    },
    /* Any filter change has to go back to page 1. The page number lives in
       Vuex and survives leaving the screen, so narrowing a search while on
       page 3 would otherwise ask for a page the narrowed results do not have
       and come back with an empty table. */
    reload() {
      this.filters.page_number = 1;
      this.getUserInsuranceList();
    },
    resetFilters() {
      const range = getDefaultSignupDateRangeIso();
      this.filters.date_range = range;
      this.filters.display_daterange = formatSignupDateRangeDisplay(range);
      this.filters.from_date_range = [];
      this.filters.display_from_daterange = "";
      this.filters.product = "";
      this.search_query = "";
      this.reload();
    },
    copyOfferLink(url) {
      if (url) {
        navigator.clipboard.writeText(url);
        this.$store.dispatch("snackbar/setSnackbar", {
          flag: true,
          text: "UTM Link Copied Successfully",
          color: "success",
        });
      } else {
        this.$store.dispatch("snackbar/setSnackbar", {
          flag: true,
          text: "UTM Link Not Found",
          color: "error",
        });
      }
    },
    openConformationDialog(item, status) {
      this.$refs.conformation_dialog.openDialog(item.id, status);
    },
    /* Both fetches go through the shared KarroFin data layer rather than the
       legacy request_GET, because that layer is the only thing that knows
       whether the rows are live or demo data. The params and the fields read
       off the response are unchanged either way -- api/normalise.js maps the
       live envelope onto the same shape. */
    getProductList() {
      api
        .getInsuranceProducts()
        .then((rows) => {
          this.product_list = rows;
        })
        // A dropdown that failed to load must not take the table down with it.
        .catch(() => {
          this.product_list = [];
        });
    },
    getUserInsuranceList() {
      const self = this;
      self.table_loading = true;
      api
        .getInsuranceList({
          page_number: this.filters.page_number,
          status: this.filters.insurance_status,
          search: this.search_query,
          insurance_product: this.filters.product,
          signup_from_date: this.filters.date_range[0],
          signup_to_date: this.filters.date_range[1],
          from_date: this.filters.from_date_range[0],
          to_date: this.filters.from_date_range[1],
        })
        .then((data) => {
          self.insurance_list = data.results;
          self.tab_data = data.counts;
          self.total_page_count = data.total_page_count;
          self.total_count =
            data.total_count !== undefined ? data.total_count : null;
        })
        .catch(() => {
          self.insurance_list = [];
        })
        .finally(() => {
          self.table_loading = false;
        });
    },
  },
  watch: {
    "filters.product": function () {
      this.reload();
    },
  },
};
</script>
